// /notificaciones — the per-user notification center. Reached from
// the top-bar bell. Groups the feed by day (Hoy / Antes) and, inside each day,
// by type: handoffs first (someone is waiting), then mentions/assignments, then
// system. Unread is shown with a dot + heavier weight + the word "Nuevo", never
// colour alone. "Marcar todas como leídas" persists via POST /notifications/
// read-all; conversation rows deep-link to /bandeja?c=<id> and mark themselves read
// via POST /notifications/:id/read. Both are optimistic then refetch.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  notifConversationId,
  type NotifDay,
  type NotifItem,
  type NotifType,
} from './notifications-data.js'
import { EmptyStateCard, PageHeader, Spinner, toast } from '../../design-system/index.js'
import { comingSoon } from '../../lib/coming-soon.js'
import { QueryError } from '../../lib/QueryError.js'

const DAYS: { id: NotifDay; label: string }[] = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'antes', label: 'Antes' },
]

const TYPE_ORDER: NotifType[] = ['handoff', 'assign', 'system']

const TYPE_META: Record<NotifType, { label: string; tone: string; icon: ReactNode }> = {
  handoff: {
    label: 'Handoffs · una conversación necesita a una persona',
    tone: 'bg-sens-bg text-sens',
    icon: (
      <path d="M10.3 3.8 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />
    ),
  },
  assign: {
    label: 'Menciones y asignaciones',
    tone: 'bg-info-bg text-info',
    icon: (
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M16 11l2 2 4-4" />
    ),
  },
  system: {
    label: 'Sistema',
    tone: 'bg-ok-bg text-ok',
    icon: (
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 15l2 2 4-4" />
    ),
  },
}

function TypeIcon({ type, small }: { type: NotifType; small?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={small ? 'size-[13px]' : 'size-[17px]'}
    >
      {TYPE_META[type].icon}
    </svg>
  )
}

function Chevron(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[15px] text-text-3"
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  )
}

function ClockIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[11px]"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}

function NotificationRow({
  item,
  onRead,
}: {
  item: NotifItem
  onRead: (id: string) => void
}): JSX.Element {
  const navigate = useNavigate()
  const action = item.action
  const convId = notifConversationId(item)
  // Conversation notifications (handoff / assignment) deep-link to the inbox with
  // that thread pre-selected; events with a fixed destination (billing) go there.
  // Both mark themselves read. Anything still without a destination keeps the
  // "coming soon" toast.
  const onAction = (): void => {
    if (convId) {
      if (item.unread) onRead(item.id)
      void navigate(`/bandeja?c=${encodeURIComponent(convId)}`)
    } else if (item.link) {
      if (item.unread) onRead(item.id)
      void navigate(item.link)
    } else if (action) comingSoon(action)
  }
  return (
    <li className="flex gap-3 border-b border-line px-4 py-3.5 last:border-b-0">
      <span
        aria-hidden
        className={`grid size-9 flex-none place-items-center rounded-[10px] ${TYPE_META[item.type].tone}`}
      >
        <TypeIcon type={item.type} />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] leading-snug text-text ${
            item.unread ? 'font-bold' : 'font-medium'
          }`}
        >
          {item.title}
          {item.unread && (
            <span className="rounded-[5px] border border-line bg-hover px-1.5 py-px text-[9.5px] font-extrabold uppercase tracking-[0.05em] text-text">
              Nuevo
            </span>
          )}
        </p>
        <p className="mt-1 text-[12px] leading-relaxed text-text-2">{item.description}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-text-3">
            <ClockIcon />
            {item.time}
          </span>
          {action && (
            <button
              type="button"
              onClick={onAction}
              className="rounded-[5px] text-[11px] font-bold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
            >
              {action}
            </button>
          )}
        </div>
      </div>
      <span className="mt-1 flex flex-none items-center gap-2.5">
        {item.unread && (
          <span aria-hidden className="size-2 flex-none rounded-full bg-brand-blue" />
        )}
        <Chevron />
      </span>
    </li>
  )
}

export function NotificationsPage(): JSX.Element {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotifications,
  })
  const [items, setItems] = useState<NotifItem[]>([])
  // The unread badge is the account-wide count from the server (accurate even
  // when it exceeds the feed's cap), adjusted optimistically as rows are read
  // and reconciled on refetch — NOT `items.filter(unread).length`, which would
  // cap at the number of rows shown.
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (data) {
      setItems(data.items)
      setUnreadCount(data.unreadCount)
    }
  }, [data])

  // Mark one row read: apply optimistically, persist, then refetch to resync.
  function markRead(id: string): void {
    setItems((cur) => {
      const wasUnread = cur.some((i) => i.id === id && i.unread)
      if (wasUnread) setUnreadCount((n) => Math.max(0, n - 1))
      return cur.map((i) => (i.id === id ? { ...i, unread: false } : i))
    })
    markNotificationRead(id)
      .catch(() => undefined)
      .finally(() => void qc.invalidateQueries({ queryKey: ['notifications'] }))
  }

  if (isError) {
    return (
      <div data-testid="notifications" className="mx-auto w-full max-w-3xl py-10">
        <QueryError
          message="No pudimos cargar tus notificaciones."
          onRetry={() => void refetch()}
        />
      </div>
    )
  }
  if (isLoading || !data) {
    return (
      <div data-testid="notifications" className="mx-auto w-full max-w-3xl py-10">
        <Spinner label="Cargando notificaciones…" />
      </div>
    )
  }

  function markAllRead(): void {
    setItems((cur) => cur.map((i) => (i.unread ? { ...i, unread: false } : i)))
    setUnreadCount(0)
    markAllNotificationsRead()
      .catch(() => {
        toast.error('No se pudieron marcar como leídas', { description: 'Inténtalo de nuevo.' })
      })
      .finally(() => void qc.invalidateQueries({ queryKey: ['notifications'] }))
  }

  return (
    <div data-testid="notifications" className="mx-auto w-full max-w-3xl">
      <PageHeader
        title="Notificaciones"
        subtitle="Lo que pasó en tu cuenta mientras no estabas. Lo importante primero."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="inline-flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-[12px] font-semibold text-text-2 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                  className="size-3.5 text-text-3"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                Marcar todas como leídas
              </button>
            )}
            <button
              type="button"
              onClick={() => void navigate('/account/notifications')}
              className="inline-flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-[12px] font-semibold text-text-2 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                className="size-3.5 text-text-3"
              >
                <path d="M12.2 2h-.4a2 2 0 0 0-2 1.7l-.2 1a7.5 7.5 0 0 0-1.7 1l-1-.4a2 2 0 0 0-2.4.9l-.3.6a2 2 0 0 0 .5 2.5l.8.6a7.6 7.6 0 0 0 0 2l-.8.6a2 2 0 0 0-.5 2.5l.3.6a2 2 0 0 0 2.4.9l1-.4a7.5 7.5 0 0 0 1.7 1l.2 1a2 2 0 0 0 2 1.7h.4a2 2 0 0 0 2-1.7l.2-1a7.5 7.5 0 0 0 1.7-1l1 .4a2 2 0 0 0 2.4-.9l.3-.6a2 2 0 0 0-.5-2.5l-.8-.6a7.6 7.6 0 0 0 0-2l.8-.6a2 2 0 0 0 .5-2.5l-.3-.6a2 2 0 0 0-2.4-.9l-1 .4a7.5 7.5 0 0 0-1.7-1l-.2-1A2 2 0 0 0 12.2 2Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Preferencias
            </button>
          </div>
        }
      />

      {unreadCount > 0 && (
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-pill border border-line bg-hover px-2.5 py-1 text-[11.5px] font-bold text-text">
          <span aria-hidden className="size-1.5 rounded-full bg-warn" />
          {unreadCount} sin leer
        </div>
      )}

      {items.length === 0 ? (
        <div className="mt-5">
          <EmptyStateCard
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.9}
                aria-hidden
              >
                <path
                  d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0M16 7l2 2 3.5-3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            title="Estás al día"
            body="No tienes notificaciones pendientes. Cuando una conversación necesite a una persona, alguien te asigne algo o termine una tarea, aparecerá aquí."
          />
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-7">
          {DAYS.map((day) => {
            const dayItems = items.filter((i) => i.day === day.id)
            if (dayItems.length === 0) return null
            return (
              <section key={day.id} aria-label={day.label}>
                <div className="mb-3 flex items-center gap-3">
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-text-3">
                    {day.label}
                  </span>
                  <span aria-hidden className="h-px flex-1 bg-line" />
                </div>
                <div className="flex flex-col gap-4">
                  {TYPE_ORDER.map((type) => {
                    const sectionItems = dayItems.filter((i) => i.type === type)
                    if (sectionItems.length === 0) return null
                    return (
                      <div key={type}>
                        <div className="mb-2 flex items-center gap-2 px-0.5 text-[11px] font-bold text-text-2">
                          <TypeIcon type={type} small />
                          <span className="min-w-0">{TYPE_META[type].label}</span>
                          <span className="rounded-pill border border-line bg-hover px-1.5 py-px text-[10px] font-bold tabular-nums text-text-3">
                            {sectionItems.length}
                          </span>
                        </div>
                        <ul className="overflow-hidden rounded-xl border border-line bg-surface">
                          {sectionItems.map((item) => (
                            <NotificationRow key={item.id} item={item} onRead={markRead} />
                          ))}
                        </ul>
                      </div>
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
